import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import styles from '../Clients.module.scss';
import { Client, ClientOpinion, exportClientDataStarted, IncompleteDocumentBehaviour, updateClientDetails } from '../store';
import { DocumentPageLimits } from './DocumentPageLimits';
import { IconButton } from '../../../shared/button/IconButton';
import { Download } from '../../../shared/icons';

interface ClientGeneralProps {
    client: Client;
}

export const ClientGeneral: React.FC<ClientGeneralProps> = ({
    client: {
        clientName,
        description,
        demoClient,
        freePages,
        maxPages,
        monthlyTotalPages,
        incompleteDocumentBehaviour
    }
}) => {
    const dispatch = useAppDispatch();

    const exportClientData = useCallback(() => dispatch(exportClientDataStarted()), [dispatch]);
    const updateClientProperties = useCallback((key: keyof Client, value: string | number[] | ClientOpinion[] | number | null) => dispatch(updateClientDetails(key, value)), [dispatch]);
    const toggleDemoClient = useCallback((checked: boolean) => updateClientProperties('demoClient', checked ? 1 : 0), [updateClientProperties]);

    const options: DropdownOption[] = [
        IncompleteDocumentBehaviour.VISIBLE_TO_ALL,
        IncompleteDocumentBehaviour.USER_AND_ACCESS_GROUPS,
        IncompleteDocumentBehaviour.USER_AND_ROLE,
        IncompleteDocumentBehaviour.USER_ONLY
    ].map(value => ({ label: value, value }));
    const selectedBehaviour = useMemo(() => options.find(({ value }) => value === incompleteDocumentBehaviour) || null, [incompleteDocumentBehaviour, options]);

    const updateDefaultDocumentView = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value: string | null = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
            updateClientProperties('incompleteDocumentBehaviour', value as IncompleteDocumentBehaviour);

        }
    }, [updateClientProperties]);

    return (
        <div className={styles.clientModalContentWrapper} data-testid='clients-modal-wrapper'>
            <div className={styles.clientModalHeader}>
                <div className={styles.clientModalTitle}>General</div>
            </div>
            <div className={styles.clientNameWrapper}>
                <div className={styles.clientNameTitle}>Client Name</div>
                <div className={styles.clientName}>
                    <Text
                        testId='client-name'
                        placeholder='Name...'
                        maxLength={256}
                        value={clientName}
                        onChange={e => updateClientProperties('clientName', e.target.value)}
                        marginBottom='0'
                    />
                </div>
            </div>
            <div className={styles.clientDescriptionWrapper}>
                <div className={styles.clientDescriptionTitle}>Description</div>
                <div className={styles.clientDescription}>
                    <Text
                        testId='client-description'
                        placeholder='Description...'
                        maxLength={256}
                        value={description}
                        onChange={e => updateClientProperties('description', e.target.value)}
                        marginBottom='0'
                    />
                </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.demoClientWrapper}>
                <div className={styles.demoClientTitle}>Demo Client with limited document upload privileges?</div>
                <div className={styles.demoClient}>
                    <Toggle
                        checked={demoClient === 1}
                        onChange={toggleDemoClient}
                    />
                </div>
            </div>
            <DocumentPageLimits freePages={freePages} maxPages={maxPages} updateClientProperties={updateClientProperties} monthlyTotalPages={monthlyTotalPages} />
            <div className={styles.divider} />
            <div className={styles.incompleteDocumentsBehaviour}>
                <div className={styles.documentBehaviourTitle}>Default incomplete document behaviour:</div>
                <div className={styles.dropdownWrapper}>
                    <Dropdown
                        value={selectedBehaviour}
                        options={options}
                        onChange={updateDefaultDocumentView}
                        isClearable={false}
                        menuPortalTarget={document.body}
                    />
                </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.downloadClientData}>
                <div className={styles.downloadClientDataLabel}>Download All Client Data</div>
                <IconButton icon={Download} onClick={exportClientData} fontSize={20} />
            </div>
        </div>
    );
};
